/**
 * G3. Буллинг (при событии `school.bullying`). Три реплики, на каждую три
 * ответа: промолчать / ответить / рассказать взрослому. Исход один на игру:
 * p = среднее по трём ответам, где «промолчать» — 0,2, «ответить» —
 * 0,3 + 0,4·отношения/100, «рассказать взрослому» — 0,6 + 0,3·отношения/100,
 * в странах с ВВП на душу ≥ BULLYING_SUPPORT_GDP ещё +0,15 (школа отвечает);
 * потолок 0,95. Удача → `stopped` (счастье +4, отношения +2), иначе
 * `continued` (счастье −3, тяжёлая запись). Блок «куда обратиться»
 * (`state.helpKey`) показывается при любом исходе.
 *
 * При `flags.skipHeavy` игра скрыта (`state.hidden`): `play` при любом вводе
 * возвращает `expected` — «рассказать взрослому» без жребия: stopped при
 * p ≥ 0,5. Сцен нет: реплики — короткие слова без описания насилия.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_SKIP_HEAVY, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { nothing } from './common';

export const BULLYING_LINES = 3;
export const BULLYING_ANSWERS = ['silent', 'reply', 'adult'] as const;
export type BullyingAnswer = (typeof BULLYING_ANSWERS)[number];
export const BULLYING_P_SILENT = 0.2;
export const BULLYING_P_REPLY_BASE = 0.3;
export const BULLYING_P_REPLY_PER_RELATIONS = 0.4;
export const BULLYING_P_ADULT_BASE = 0.6;
export const BULLYING_P_ADULT_PER_RELATIONS = 0.3;
/** ВВП на душу (ППС), с которого школа «отвечает» на обращение. */
export const BULLYING_SUPPORT_GDP = 15_000;
export const BULLYING_SUPPORT_BONUS = 0.15;
export const BULLYING_P_MAX = 0.95;
export const BULLYING_STOPPED_STATS = { happiness: 4, relations: 2 } as const;
export const BULLYING_CONTINUED_STATS = { happiness: -3 } as const;
export const BULLYING_HELP_KEY = 'minigame.bullying.help';

export interface BullyingState {
  /** Ключи реплик `minigame.bullying.line.<i>`; ответы — `<line>.<answer>`. */
  lines: string[];
  answers: readonly BullyingAnswer[];
  relations: number;
  /** Школа отвечает на обращение (страна с достатком). */
  support: boolean;
  helpKey: string;
  /** `flags.skipHeavy`: только текст и ожидаемый исход. */
  hidden: boolean;
}

/** Индексы ответов по репликам (0 промолчать, 1 ответить, 2 рассказать взрослому). */
export type BullyingInput = { picks: number[] };

export function bullyingLineKey(line: number): string {
  return `minigame.bullying.line.${line}`;
}

export function bullyingAnswerP(state: BullyingState, answer: BullyingAnswer): number {
  const r = Math.min(100, Math.max(0, state.relations)) / 100;
  let p: number;
  switch (answer) {
    case 'silent':
      p = BULLYING_P_SILENT;
      break;
    case 'reply':
      p = BULLYING_P_REPLY_BASE + BULLYING_P_REPLY_PER_RELATIONS * r;
      break;
    case 'adult':
      p = BULLYING_P_ADULT_BASE + BULLYING_P_ADULT_PER_RELATIONS * r + (state.support ? BULLYING_SUPPORT_BONUS : 0);
      break;
  }
  return Math.min(BULLYING_P_MAX, p);
}

function result(stopped: boolean): MinigameOutcome {
  if (stopped) return { ...nothing(), stats: { ...BULLYING_STOPPED_STATS }, logKey: 'minigame.bullying.stopped' };
  return { ...nothing(), stats: { ...BULLYING_CONTINUED_STATS }, logKey: 'minigame.bullying.continued', heavy: true };
}

function meanP(state: BullyingState, picks: unknown): number {
  const list = Array.isArray(picks) ? picks : [];
  let sum = 0;
  for (let i = 0; i < state.lines.length; i += 1) {
    const pick = list[i];
    const answer = Number.isInteger(pick) && pick >= 0 && pick < state.answers.length ? (state.answers[pick] as BullyingAnswer) : 'silent';
    sum += bullyingAnswerP(state, answer);
  }
  return sum / state.lines.length;
}

export const bullying: MinigameDef<BullyingInput, BullyingState> = {
  id: 'bullying',
  section: 'G',
  triggers: [{ kind: 'event', event: 'school.bullying' }],
  available(_life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return true;
  },
  setup(life: Life, ds: Dataset): BullyingState {
    const lines: string[] = [];
    for (let i = 0; i < BULLYING_LINES; i += 1) lines.push(bullyingLineKey(i));
    return {
      lines,
      answers: BULLYING_ANSWERS,
      relations: life.stats.relations,
      support: gdpPpp(ds, life) >= BULLYING_SUPPORT_GDP,
      helpKey: BULLYING_HELP_KEY,
      hidden: life.flags[FLAG_SKIP_HEAVY] === true,
    };
  },
  play(state: BullyingState, input: BullyingInput, life: Life, rng: Rng): MinigameOutcome {
    if (state.hidden || life.flags[FLAG_SKIP_HEAVY] === true) return bullying.expected(state, life);
    return result(rng.chance(meanP(state, input.picks)));
  },
  expected(state: BullyingState): MinigameOutcome {
    return result(bullyingAnswerP(state, 'adult') >= 0.5);
  },
};
